import React, { useCallback, useState } from "react";
import moment from "moment";
import Tooltip from "@material-ui/core/Tooltip";
import { getInvoiceDetail } from "../redux/action/invoiceAction";

const INVOICE_STATUS_LABELS = {
  0: "Pending",
  1: "Paid",
  2: "Partially Paid",
  3: "Closed",
  4: "Cancelled",
  null: "Pending",
};

export function getInvoicePaymentStatusLabel(invoice) {
  const inv = invoice || {};
  if (Number(inv.status) === 4) return "Cancelled";

  const totalNum = parseFloat(inv.total || 0);
  const amountPaidNum = parseFloat(inv.amount_paid || 0);
  const amountDueNum = Math.max(totalNum - amountPaidNum, 0);

  if (amountDueNum === 0 && totalNum > 0) return "Paid";
  if (amountPaidNum > 0 && amountDueNum > 0) return "Partially Paid";
  if (amountDueNum > 0) return "Pending";

  return INVOICE_STATUS_LABELS[inv.status] || "Pending";
}

export function isInvoiceCancelled(invoice) {
  const inv = invoice || {};
  if (Number(inv.status) === 4) return true;

  const statusText = String(inv.status ?? "")
    .trim()
    .toLowerCase();
  if (statusText === "cancelled" || statusText === "4") return true;

  const paymentStatus = String(
    inv.payment_status ?? inv.paymentStatus ?? "",
  )
    .trim()
    .toLowerCase();
  if (paymentStatus === "cancelled") return true;

  return getInvoicePaymentStatusLabel(invoice) === "Cancelled";
}

export function getInvoiceCancellationComment(invoice) {
  const inv = invoice || {};
  const candidates = [
    inv.cancellation_comment,
    inv.cancellationComment,
    inv.cancel_comment,
    inv.cancelComment,
    inv.cancellation_reason,
    inv.cancellationReason,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const s = String(candidates[i] ?? "").trim();
    if (s) return s;
  }
  return "";
}

const PAYMENT_METHOD_LABELS = {
  Zelle: "Zelle",
  Check: "Check",
  ACH: "ACH",
  "Authorize.Net": "Authorize.Net",
  QuickBooks: "QuickBooks",
  none: "Not Yet paid",
  "Not Yet paid": "Not Yet paid",
};

export function getInvoicePaidDateRaw(invoice) {
  const inv = invoice || {};
  const raw =
    inv.paid_date ??
    inv.paidDate ??
    inv.payment_date ??
    inv.paymentDate ??
    null;
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s || null;
}

export function paidDateToApiValue(value) {
  if (value === null || value === undefined) return "";
  const s = String(value).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = moment(
    s,
    ["YYYY-MM-DD", "MMM DD yyyy", "MMM DD YYYY", "MM/DD/YYYY"],
    true,
  );
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
}

export function formatInvoicePaidDateDisplay(invoice) {
  const raw = getInvoicePaidDateRaw(invoice);
  if (!raw) return "—";
  const apiValue = paidDateToApiValue(raw);
  if (!apiValue) return "—";
  return moment(apiValue, "YYYY-MM-DD", true).format("MM/DD/YYYY");
}

export function shouldShowInvoicePaidDate(invoice) {
  const status = getInvoicePaymentStatusLabel(invoice);
  return status === "Paid" || status === "Partially Paid";
}

export function getInvoicePaymentHistory(invoice) {
  let raw = invoice?.partial_payments ?? invoice?.partialPayments;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      raw = JSON.parse(trimmed);
    } catch (_err) {
      return [];
    }
  }
  const list = Array.isArray(raw) ? raw : [];
  return list
    .filter((row) => Number(row?.amount) > 0)
    .map((row) => ({
      payment_date: row?.payment_date || row?.date || "",
      payment_method: row?.payment_method || row?.method || "",
      amount: row?.amount,
    }));
}

export function getInvoicePaymentMethodLabel(invoice) {
  const inv = invoice || {};
  const raw = String(
    inv.payment_method ??
      inv.paymentMethod ??
      inv.payment_mode ??
      inv.paymentMode ??
      "",
  ).trim();

  if (!raw) return "—";
  return PAYMENT_METHOD_LABELS[raw] || raw;
}

/**
 * Cancelled status badge with hover tooltip. Fetches detail on hover when the
 * list payload omits cancellation_comment.
 */
export function CancelledInvoiceStatusBadge({
  invoice,
  invoiceId,
  label = "Cancelled",
}) {
  const initialComment = getInvoiceCancellationComment(invoice);
  const [tooltipText, setTooltipText] = useState(
    initialComment || "No cancellation comment recorded",
  );
  const fetchStartedRef = React.useRef(false);

  const resolveTooltip = useCallback(async () => {
    if (initialComment || fetchStartedRef.current) return;
    const id =
      invoiceId ||
      invoice?.client_invoice_id ||
      invoice?.clientInvoiceId;
    if (!id) return;

    fetchStartedRef.current = true;
    setTooltipText("Loading comment...");
    try {
      const response = await getInvoiceDetail(id);
      const detail = response?.data?.data || response?.data || {};
      const comment = getInvoiceCancellationComment(detail);
      setTooltipText(comment || "No cancellation comment recorded");
    } catch {
      setTooltipText("No cancellation comment recorded");
    }
  }, [initialComment, invoice, invoiceId]);

  return (
    <Tooltip
      title={tooltipText}
      arrow
      placement="top"
      enterDelay={200}
      classes={{ tooltip: "invoice-cancellation-comment-tooltip" }}
    >
      <span
        className="invoice-status-cancelled-tooltip-wrap"
        style={{ display: "inline-block" }}
        onMouseEnter={resolveTooltip}
        onFocus={resolveTooltip}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className="invoice-status-cancelled-tooltip status-badge status-cancelled">
          {label}
        </span>
      </span>
    </Tooltip>
  );
}

export function renderInvoicePaymentStatusCell(invoice, label) {
  const text = String(label ?? "").trim() || "—";
  const slug = text.toLowerCase().replace(/\s+/g, "");

  if (text !== "Cancelled") {
    return <span className={`status-badge status-${slug}`}>{text}</span>;
  }

  return (
    <CancelledInvoiceStatusBadge
      invoice={invoice}
      invoiceId={invoice?.client_invoice_id}
      label={text}
    />
  );
}

export function renderInvoicePaymentStatusInTable(row, columnKey, label) {
  const text = String(label ?? "").trim() || "—";
  const slug = text.toLowerCase().replace(/\s+/g, "");

  if (text !== "Cancelled") {
    return <span className={`status-badge status-${slug}`}>{text}</span>;
  }

  const invoice = row._invoiceData || row._invoiceRaw || row;
  const invoiceId =
    invoice?.client_invoice_id ||
    row._invoiceData?.client_invoice_id ||
    row._invoiceRaw?.client_invoice_id;

  return (
    <CancelledInvoiceStatusBadge
      invoice={invoice}
      invoiceId={invoiceId}
      label={text}
    />
  );
}
